/**
 * Typed Variable Editor — Phase 5B/5C (#150 #151).
 *
 * Replaces the raw JSON textarea on skills + prompts with a structured
 * row-based editor: name, type, required, description, default, and
 * (for prompts) options for select-type vars.
 *
 * Two flavors via the `flavor` prop:
 *   - 'skill'  — variables on studio_skills:
 *                  { name, type: string|number|boolean, description, required }
 *   - 'prompt' — variables on prompt_templates:
 *                  { name, label, type, required, default, options? }
 */

import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

// ─── Skill variable shape (matches studio_skills.variables) ────────────────

export interface SkillVariableEntry {
  name: string;
  type: 'string' | 'number' | 'boolean';
  description: string;
  required?: boolean;
}

// ─── Prompt variable shape (matches prompt_templates.variables) ───────────

export interface PromptVariableEntry {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'number' | 'textarea' | 'boolean';
  required: boolean;
  default?: string;
  options?: string[];
}

interface SkillProps {
  flavor: 'skill';
  value: SkillVariableEntry[];
  onChange: (next: SkillVariableEntry[]) => void;
  disabled?: boolean;
}
interface PromptProps {
  flavor: 'prompt';
  value: PromptVariableEntry[];
  onChange: (next: PromptVariableEntry[]) => void;
  disabled?: boolean;
}
export type VariableEditorProps = SkillProps | PromptProps;

const SKILL_TYPES: SkillVariableEntry['type'][] = ['string', 'number', 'boolean'];
const PROMPT_TYPES: PromptVariableEntry['type'][] = ['text', 'textarea', 'select', 'date', 'number', 'boolean'];

const EMPTY_SKILL: SkillVariableEntry = {
  name: '', type: 'string', description: '', required: false,
};
const EMPTY_PROMPT: PromptVariableEntry = {
  name: '', label: '', type: 'text', required: false, default: '',
};

export function VariableEditor(props: VariableEditorProps) {
  const { flavor, disabled } = props;

  const add = () => {
    if (flavor === 'skill') {
      props.onChange([...(props.value as SkillVariableEntry[]), { ...EMPTY_SKILL }]);
    } else {
      props.onChange([...(props.value as PromptVariableEntry[]), { ...EMPTY_PROMPT }]);
    }
  };

  const remove = (i: number) => {
    if (flavor === 'skill') {
      props.onChange((props.value as SkillVariableEntry[]).filter((_, idx) => idx !== i));
    } else {
      props.onChange((props.value as PromptVariableEntry[]).filter((_, idx) => idx !== i));
    }
  };

  const updateSkill = <K extends keyof SkillVariableEntry>(
    i: number, field: K, val: SkillVariableEntry[K],
  ) => {
    const next = [...(props.value as SkillVariableEntry[])];
    next[i] = { ...next[i], [field]: val };
    (props as SkillProps).onChange(next);
  };

  const updatePrompt = <K extends keyof PromptVariableEntry>(
    i: number, field: K, val: PromptVariableEntry[K],
  ) => {
    const next = [...(props.value as PromptVariableEntry[])];
    next[i] = { ...next[i], [field]: val };
    (props as PromptProps).onChange(next);
  };

  if (flavor === 'skill') {
    const rows = props.value as SkillVariableEntry[];
    return (
      <div className="space-y-2">
        {rows.length === 0 ? (
          <div className="text-[11px] text-muted-foreground italic border border-dashed rounded p-3 text-center">
            No variables. Click <strong>Add variable</strong> to start.
          </div>
        ) : rows.map((v, i) => {
          // Round 6 fix STUDIO-R6-002 (horizontal sweep) — blank
          // variable names are invalid: they prevent template
          // substitution and persist as broken metadata. Skill
          // variables share the same template shape as prompt
          // variables, so the same gate applies.
          const blankName = !v.name.trim();
          return (
          <div key={i} className={cn(
            'grid grid-cols-[1fr_120px_auto_24px] gap-2 items-start border rounded p-2',
            blankName && 'border-destructive/40',
          )}>
            <div className="space-y-1">
              <Input
                value={v.name}
                onChange={(e) => updateSkill(i, 'name', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                placeholder="variable_name"
                disabled={disabled}
                className={cn(
                  'font-mono text-xs h-7',
                  blankName && 'border-destructive focus-visible:ring-destructive',
                )}
                aria-invalid={blankName || undefined}
                aria-describedby={blankName ? `sk-var-name-error-${i}` : undefined}
                aria-label={`Name for variable ${i + 1}`}
              />
              {blankName && (
                <p id={`sk-var-name-error-${i}`} className="text-[10px] text-destructive flex items-start gap-1">
                  <AlertCircle className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                  <span>Name is required.</span>
                </p>
              )}
              <Input
                value={v.description}
                onChange={(e) => updateSkill(i, 'description', e.target.value)}
                placeholder="What this variable means"
                disabled={disabled}
                className="text-xs h-7"
                aria-label={
                  v.name ? `Description for ${v.name}` : `Description for variable ${i + 1}`
                }
              />
            </div>
            <Select
              value={v.type}
              onValueChange={(val) => updateSkill(i, 'type', val as SkillVariableEntry['type'])}
              disabled={disabled}
            >
              <SelectTrigger
                className="h-7 text-xs"
                aria-label={v.name ? `Type for ${v.name}` : `Type for variable ${i + 1}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SKILL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 h-7 text-[10px]">
              <Switch
                checked={!!v.required}
                onCheckedChange={(val) => updateSkill(i, 'required', val)}
                disabled={disabled}
                className="scale-75"
              />
              required
            </label>
            <Button
              variant="ghost"
              size="sm"
              aria-label={v.name ? `Remove variable ${v.name}` : `Remove variable ${i + 1}`}
              title={v.name ? `Remove variable ${v.name}` : 'Remove variable'}
              className="h-7 w-6 p-0 text-destructive hover:bg-destructive/10"
              onClick={() => remove(i)}
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          );
        })}
        <Button
          variant="outline"
          size="sm"
          onClick={add}
          disabled={disabled}
          className="gap-1 h-7 text-xs"
        >
          <Plus className="h-3 w-3" /> Add variable
        </Button>
      </div>
    );
  }

  // prompt
  const rows = props.value as PromptVariableEntry[];
  return (
    <div className="space-y-2">
      {rows.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic border border-dashed rounded p-3 text-center">
          No variables. Click <strong>Add variable</strong> to start.
        </div>
      ) : rows.map((v, i) => {
        // Round 6 fix STUDIO-R6-002 — blank prompt-variable names
        // produce broken `{{}}` substitution at preview/runtime.
        // Block save in the parent; render an inline error here.
        const blankName = !v.name.trim();
        return (
        <div key={i} className={cn(
          'border rounded p-2 space-y-2',
          blankName && 'border-destructive/40',
        )}>
          <div className="grid grid-cols-[1fr_1fr_120px_auto_24px] gap-2 items-start">
            <div className="space-y-1">
              <Label htmlFor={`pr-var-name-${i}`} className="text-[10px]">Name</Label>
              <Input
                id={`pr-var-name-${i}`}
                value={v.name}
                onChange={(e) => updatePrompt(i, 'name', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                placeholder="variable_name"
                disabled={disabled}
                className={cn(
                  'font-mono text-xs h-7',
                  blankName && 'border-destructive focus-visible:ring-destructive',
                )}
                aria-invalid={blankName || undefined}
                aria-describedby={blankName ? `pr-var-name-error-${i}` : undefined}
                aria-label={`Name for variable ${i + 1}`}
              />
              {blankName && (
                <p id={`pr-var-name-error-${i}`} className="text-[10px] text-destructive flex items-start gap-1">
                  <AlertCircle className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                  <span>Name is required — placeholders won't substitute.</span>
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor={`pr-var-label-${i}`} className="text-[10px]">Label</Label>
              <Input
                id={`pr-var-label-${i}`}
                value={v.label}
                onChange={(e) => updatePrompt(i, 'label', e.target.value)}
                placeholder="Display label"
                disabled={disabled}
                className="text-xs h-7"
                aria-label={
                  v.name ? `Display label for ${v.name}` : `Display label for variable ${i + 1}`
                }
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`pr-var-type-${i}`} className="text-[10px]">Type</Label>
              <Select
                value={v.type}
                onValueChange={(val) => updatePrompt(i, 'type', val as PromptVariableEntry['type'])}
                disabled={disabled}
              >
                <SelectTrigger
                  id={`pr-var-type-${i}`}
                  className="h-7 text-xs"
                  aria-label={v.name ? `Type for ${v.name}` : `Type for variable ${i + 1}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROMPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-1.5 h-7 mt-4 text-[10px]">
              <Switch
                checked={v.required}
                onCheckedChange={(val) => updatePrompt(i, 'required', val)}
                disabled={disabled}
                className="scale-75"
              />
              required
            </label>
            <Button
              variant="ghost"
              size="sm"
              aria-label={v.name ? `Remove variable ${v.name}` : `Remove variable ${i + 1}`}
              title={v.name ? `Remove variable ${v.name}` : 'Remove variable'}
              className="h-7 w-6 p-0 mt-4 text-destructive hover:bg-destructive/10"
              onClick={() => remove(i)}
              disabled={disabled}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          <div className="grid grid-cols-[1fr_1fr] gap-2">
            {/* Round 4 fix #249 — each Default / Options input gets a
                unique accessible name composed from the variable name
                so screen-reader users can distinguish them when many
                variables are listed. The visual <Label> stays generic
                and is bound via htmlFor so pointer / keyboard focus
                still works as before. */}
            <div>
              <Label htmlFor={`pr-var-default-${i}`} className="text-[10px]">Default</Label>
              <Input
                id={`pr-var-default-${i}`}
                value={v.default ?? ''}
                onChange={(e) => updatePrompt(i, 'default', e.target.value)}
                placeholder="(optional)"
                disabled={disabled}
                className="text-xs h-7"
                aria-label={
                  v.name
                    ? `Default value for ${v.name}`
                    : `Default value for variable ${i + 1}`
                }
              />
            </div>
            {v.type === 'select' && (
              <div>
                <Label htmlFor={`pr-var-options-${i}`} className="text-[10px]">Options (comma-separated)</Label>
                <Input
                  id={`pr-var-options-${i}`}
                  value={(v.options ?? []).join(', ')}
                  onChange={(e) =>
                    updatePrompt(
                      i,
                      'options',
                      e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    )
                  }
                  placeholder="day, swing, night"
                  disabled={disabled}
                  className="text-xs h-7"
                  aria-label={
                    v.name
                      ? `Options for ${v.name}`
                      : `Options for variable ${i + 1}`
                  }
                />
              </div>
            )}
          </div>
        </div>
        );
      })}
      <Button
        variant="outline"
        size="sm"
        onClick={add}
        disabled={disabled}
        className="gap-1 h-7 text-xs"
      >
        <Plus className="h-3 w-3" /> Add variable
      </Button>
    </div>
  );
}

/**
 * Round 6 fix STUDIO-R6-002 — shared helper for the parent pages
 * (prompts.tsx + skills.tsx) to gate save on no-blank-name rows.
 * Same predicate used inside the editor for inline error display.
 */
export function variablesHaveBlankNames(
  vars: ReadonlyArray<{ name: string }>,
): boolean {
  return vars.some((v) => !v.name.trim());
}

// ─── Preview helper for prompts: substitute {{var}} with provided values ───

export function previewPromptTemplate(
  template: string,
  variables: PromptVariableEntry[],
  values: Record<string, string>,
): { rendered: string; missing: string[] } {
  const missing: string[] = [];
  let rendered = template;
  for (const v of variables) {
    const value = values[v.name] ?? v.default ?? '';
    if (v.required && !value) missing.push(v.name);
    const re = new RegExp(`\\{\\{\\s*${v.name}\\s*\\}\\}`, 'g');
    rendered = rendered.replace(re, value);
  }
  return { rendered, missing };
}

// ─── Preview helper for skills (#150 phase 5B follow-up) ───────────────────
//
// Skills variables don't carry a `default` field on the wire (the skill
// flavor schema is { name, type, description, required? }), but the Studio
// sheet still benefits from showing how `{{var}}` placeholders in the
// instructions body will substitute at run-time. We render a placeholder
// token (e.g. `<value of equipment_id>`) for each declared variable that
// has no value supplied, so users can see immediately which variables are
// wired into the body and which are dangling.
//
// Returns:
//   rendered  — the instructions body with every `{{name}}` replaced
//   missing   — variables declared but never referenced in the body
//   undeclared — placeholders found in the body that have no matching
//               variable declaration (typo detector)
export function previewSkillTemplate(
  instructions: string,
  variables: SkillVariableEntry[],
  values: Record<string, string> = {},
): { rendered: string; missing: string[]; undeclared: string[] } {
  const missing: string[] = [];
  let rendered = instructions;
  const seen = new Set<string>();
  for (const v of variables) {
    if (!v.name.trim()) continue;
    seen.add(v.name);
    const value = values[v.name];
    const re = new RegExp(`\\{\\{\\s*${v.name}\\s*\\}\\}`, 'g');
    const matched = re.test(rendered);
    // Reset lastIndex since `test` is stateful on /g regexes.
    re.lastIndex = 0;
    if (!matched) {
      missing.push(v.name);
      continue;
    }
    rendered = rendered.replace(
      re,
      value && value.length > 0 ? value : `<value of ${v.name}>`,
    );
  }
  // Find any {{token}} still present in `rendered` that wasn't declared.
  const undeclared: string[] = [];
  const placeholderRe = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = placeholderRe.exec(rendered)) !== null) {
    const tokenName = m[1];
    if (!seen.has(tokenName) && !undeclared.includes(tokenName)) {
      undeclared.push(tokenName);
    }
  }
  return { rendered, missing, undeclared };
}

/**
 * Variable-name validator (#150 phase 5B follow-up).
 *
 * Skill (and prompt) variables become `{{name}}` placeholders inside the
 * instructions body. To keep template substitution unambiguous we constrain
 * names to lower-snake-case starting with a letter or underscore:
 *
 *   - first char: a-z or _
 *   - subsequent chars: a-z, 0-9, or _
 *   - reject empty / blank / leading-digit / uppercase / dashes / dots /
 *     whitespace
 *
 * Returns `{ ok: true }` on valid input or
 * `{ ok: false, message }` describing the constraint that failed.
 *
 * NOTE: The inline `<Input onChange>` in this file already strips non-word
 * characters at the keystroke layer so a user typing free-form into the
 * field cannot enter invalid characters. This validator is the canonical
 * server-side-aligned check used by save handlers, helper-panel mutations,
 * and unit tests.
 */
export function validateSkillVariableName(
  name: string,
): { ok: true } | { ok: false; message: string } {
  if (typeof name !== 'string' || name.trim() === '') {
    return { ok: false, message: 'Name is required.' };
  }
  // Reject if any whitespace anywhere.
  if (/\s/.test(name)) {
    return { ok: false, message: 'Name cannot contain whitespace.' };
  }
  // Reject leading digit.
  if (/^[0-9]/.test(name)) {
    return { ok: false, message: 'Name cannot start with a digit.' };
  }
  if (!/^[a-z_][a-z0-9_]*$/.test(name)) {
    return {
      ok: false,
      message:
        'Name must be lower-snake-case (a-z, 0-9, _) and start with a letter or underscore.',
    };
  }
  return { ok: true };
}

/**
 * Returns the subset of variables with invalid names. Used by save
 * handlers and the sheet header to surface a tooltip explaining why the
 * Save button is disabled. Differs from `variablesHaveBlankNames` in that
 * it also catches uppercase / leading-digit / non-conforming names — both
 * predicates are needed because the inline keystroke filter only strips
 * unsupported characters, it does not block invalid arrangements (e.g.
 * `1abc` is reachable via paste).
 */
export function findInvalidSkillVariableNames(
  vars: ReadonlyArray<{ name: string }>,
): string[] {
  return vars
    .map((v) => v.name)
    .filter((n) => !validateSkillVariableName(n).ok);
}

export function VariableValueForm({
  variables,
  values,
  onChange,
  disabled,
}: {
  variables: PromptVariableEntry[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
}) {
  if (variables.length === 0) return null;
  return (
    <div className="space-y-2">
      {variables.map((v) => (
        <div key={v.name} className="space-y-1">
          <Label className="text-xs flex items-center gap-1.5">
            {v.label || v.name}
            {v.required && <Badge variant="outline" className="text-[8px] bg-amber-500/10 text-amber-700 border-amber-500/30">required</Badge>}
            <span className="font-mono text-[10px] text-muted-foreground">{v.type}</span>
          </Label>
          {v.type === 'select' ? (
            <Select
              value={values[v.name] ?? v.default ?? ''}
              onValueChange={(val) => onChange({ ...values, [v.name]: val })}
              disabled={disabled}
            >
              <SelectTrigger
                className="h-8 text-sm"
                aria-label={v.label || v.name || 'Variable value'}
              >
                <SelectValue placeholder="(choose…)" />
              </SelectTrigger>
              <SelectContent>
                {(v.options ?? []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : v.type === 'textarea' ? (
            <textarea
              value={values[v.name] ?? v.default ?? ''}
              onChange={(e) => onChange({ ...values, [v.name]: e.target.value })}
              disabled={disabled}
              rows={3}
              className="w-full text-sm rounded border bg-background px-2 py-1"
            />
          ) : (
            <Input
              type={v.type === 'number' ? 'number' : v.type === 'date' ? 'date' : 'text'}
              value={values[v.name] ?? v.default ?? ''}
              onChange={(e) => onChange({ ...values, [v.name]: e.target.value })}
              disabled={disabled}
              className="text-sm h-8"
            />
          )}
        </div>
      ))}
    </div>
  );
}
